/**
 * Centralized error handling utility for Supabase real-time subscriptions
 * Provides retry logic, error tracking, and user-friendly error messages
 */

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;
const EXPONENTIAL_BACKOFF_MULTIPLIER = 2;

/**
 * Error types for real-time subscriptions
 */
export const RealtimeErrorType = {
  CONNECTION_FAILED: 'connection_failed',
  SUBSCRIPTION_ERROR: 'subscription_error',
  DATA_FETCH_ERROR: 'data_fetch_error',
  CHANNEL_ERROR: 'channel_error',
  TIMEOUT: 'timeout'
};

/**
 * User-friendly error messages
 */
const ERROR_MESSAGES = {
  [RealtimeErrorType?.CONNECTION_FAILED]: 'Unable to establish real-time connection. Retrying...',
  [RealtimeErrorType?.SUBSCRIPTION_ERROR]: 'Real-time updates temporarily unavailable. Retrying...',
  [RealtimeErrorType?.DATA_FETCH_ERROR]: 'Failed to load data. Please refresh the page.',
  [RealtimeErrorType?.CHANNEL_ERROR]: 'Real-time channel error. Reconnecting...',
  [RealtimeErrorType?.TIMEOUT]: 'Connection timeout. Attempting to reconnect...'
};

/**
 * Normalize error to always be a proper Error instance
 */
function normalizeError(error, fallbackMessage) {
  if (error instanceof Error) return error;
  if (error && typeof error === 'object') return Object.assign(new Error(error.message || fallbackMessage || 'Unknown error'), error);
  return new Error(error || fallbackMessage || 'Unknown error');
}

/**
 * Retry manager for handling subscription reconnections
 */
class RetryManager {
  constructor() {
    this.retryAttempts = new Map();
    this.retryTimeouts = new Map();
  }

  getAttemptCount(channelName) {
    return this.retryAttempts?.get(channelName) || 0;
  }

  incrementAttempt(channelName) {
    const current = this.getAttemptCount(channelName);
    this.retryAttempts?.set(channelName, current + 1);
    return current + 1;
  }

  resetAttempts(channelName) {
    this.retryAttempts?.delete(channelName);
    this.clearTimeout(channelName);
  }

  clearTimeout(channelName) {
    const timeout = this.retryTimeouts?.get(channelName);
    if (timeout) {
      clearTimeout(timeout);
      this.retryTimeouts?.delete(channelName);
    }
  }

  setRetryTimeout(channelName, callback, delay) {
    this.clearTimeout(channelName);
    const timeout = setTimeout(callback, delay);
    this.retryTimeouts?.set(channelName, timeout);
    return timeout;
  }

  cleanup(channelName) {
    this.resetAttempts(channelName);
  }
}

const retryManager = new RetryManager();

/**
 * Calculate exponential backoff delay
 */
function getRetryDelay(attemptNumber) {
  return RETRY_DELAY_MS * Math.pow(EXPONENTIAL_BACKOFF_MULTIPLIER, attemptNumber - 1);
}

/**
 * Refresh the Supabase session and update the realtime access token
 */
async function refreshSessionToken(supabase) {
  try {
    const { data, error } = await supabase?.auth?.refreshSession();
    if (error) throw error;
    // Push the fresh access token into the realtime transport so channels
    // authenticate with the new JWT immediately
    if (data?.session?.access_token) {
      supabase?.realtime?.setAuth(data?.session?.access_token);
    }
    return true;
  } catch (err) {
    console.warn('Session refresh failed:', err?.message);
    return false;
  }
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(errorType) {
  return ERROR_MESSAGES?.[errorType] || 'An unexpected error occurred. Please try again.';
}

/**
 * Create a real-time subscription with error handling and retry logic
 */
export function createRealtimeSubscription({
  supabase,
  channelName,
  tableName,
  onDataChange,
  onError,
  filter = null,
  event = '*'
}) {
  if (!supabase || !channelName || !tableName || !onDataChange) {
    console.error('Missing required parameters for real-time subscription');
    return { channel: null, cleanup: () => {} };
  }

  let channel = null;
  let isActive = true;

  const setupSubscription = async () => {
    try {
      // Always ensure we have a fresh token before subscribing
      await refreshSessionToken(supabase);

      // Remove any existing channel with this name before creating a new one.
      // Supabase reuses channels by name, so calling .on() on an already-subscribed
      // channel throws "cannot add postgres_changes callbacks after subscribe()".
      const existingChannel = supabase?.getChannels()?.find(
        ch => ch?.topic === channelName || ch?.topic === `realtime:${channelName}`
      );
      if (existingChannel) {
        await supabase?.removeChannel(existingChannel);
      }

      const channelConfig = {
        event,
        schema: 'public',
        table: tableName
      };

      if (filter) {
        channelConfig.filter = filter;
      }

      channel = supabase?.channel(channelName)?.on('postgres_changes', channelConfig, async (payload) => {
          try {
            retryManager?.resetAttempts(channelName);
            await onDataChange(payload);
          } catch (error) {
            const normalizedError = normalizeError(error, `Data change error for ${tableName}`);
            console.error(`Error handling data change for ${tableName}:`, normalizedError);
            if (onError) {
              onError({
                type: RealtimeErrorType?.DATA_FETCH_ERROR,
                message: getErrorMessage(RealtimeErrorType?.DATA_FETCH_ERROR),
                error: normalizedError
              });
            }
          }
        })?.subscribe((status, error) => {
          if (status === 'SUBSCRIBED') {
            console.log(`✓ Real-time subscription active: ${channelName}`);
            retryManager?.resetAttempts(channelName);
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            console.error(`Real-time subscription error for ${channelName}:`, status, error);
            handleSubscriptionError(status, error);
          }
        });

      return channel;
    } catch (error) {
      const normalizedError = normalizeError(error, `Setup error for ${channelName}`);
      console.error(`Failed to setup subscription for ${channelName}:`, normalizedError);
      handleSubscriptionError('SETUP_ERROR', normalizedError);
      return null;
    }
  };

  const handleSubscriptionError = (status, error) => {
    if (!isActive) return;

    const normalizedError = normalizeError(error, `Subscription error: ${status}`);

    const errorMessage = normalizedError?.message || '';
    const isJwtError = errorMessage?.toLowerCase()?.includes('jwt') ||
      errorMessage?.toLowerCase()?.includes('token') ||
      errorMessage?.toLowerCase()?.includes('expired') ||
      status === 'CHANNEL_ERROR';

    // For JWT/token errors, reset the attempt counter so we get a full set of
    // retries after the refresh rather than burning through them immediately
    if (isJwtError) {
      retryManager?.resetAttempts(channelName);
    }

    const attemptCount = retryManager?.incrementAttempt(channelName);

    let errorType = RealtimeErrorType?.SUBSCRIPTION_ERROR;
    if (status === 'TIMED_OUT') errorType = RealtimeErrorType?.TIMEOUT;
    if (status === 'CHANNEL_ERROR') errorType = RealtimeErrorType?.CHANNEL_ERROR;

    const willRetry = attemptCount < MAX_RETRY_ATTEMPTS;

    if (willRetry) {
      console.warn(`Real-time subscription issue for ${channelName} (attempt ${attemptCount}/${MAX_RETRY_ATTEMPTS}):`, status);
    } else {
      console.error(`Max retry attempts reached for ${channelName}`);
    }

    if (onError) {
      onError({
        type: errorType,
        message: getErrorMessage(errorType),
        error: normalizedError,
        attemptCount,
        willRetry
      });
    }

    if (willRetry) {
      const delay = getRetryDelay(attemptCount);
      console.log(`Retrying subscription ${channelName} in ${delay}ms (attempt ${attemptCount}/${MAX_RETRY_ATTEMPTS})`);

      retryManager?.setRetryTimeout(channelName, async () => {
        if (isActive) {
          if (isJwtError) {
            const refreshed = await refreshSessionToken(supabase);
            if (refreshed) {
              console.log(`Session refreshed before retrying ${channelName}`);
            }
          }

          if (channel) {
            supabase?.removeChannel(channel);
            channel = null;
          }
          channel = await setupSubscription();
        }
      }, delay);
    } else {
      if (onError) {
        onError({
          type: RealtimeErrorType?.CONNECTION_FAILED,
          message: 'Unable to establish real-time connection. Please refresh the page.',
          error: normalizedError,
          attemptCount,
          willRetry: false
        });
      }
    }
  };

  // setupSubscription is now async; fire and capture the promise
  setupSubscription()?.then(ch => { channel = ch; });

  const cleanup = () => {
    isActive = false;
    retryManager?.cleanup(channelName);
    if (channel) {
      supabase?.removeChannel(channel);
      channel = null;
    }
  };

  return { channel, cleanup };
}

/**
 * Create multiple subscriptions with shared error handling
 */
export function createMultipleSubscriptions(subscriptions) {
  const cleanupFunctions = subscriptions?.map(config => {
    const { cleanup } = createRealtimeSubscription(config);
    return cleanup;
  });

  return () => {
    cleanupFunctions?.forEach(cleanup => cleanup());
  };
}

/**
 * Hook-friendly error state manager
 */
export function createErrorStateManager() {
  const errors = new Map();

  return {
    addError: (channelName, error) => {
      errors?.set(channelName, {
        ...error,
        timestamp: Date.now()
      });
    },
    removeError: (channelName) => {
      errors?.delete(channelName);
    },
    getError: (channelName) => {
      return errors?.get(channelName);
    },
    hasErrors: () => {
      return errors?.size > 0;
    },
    getAllErrors: () => {
      return Array.from(errors?.values());
    },
    clear: () => {
      errors?.clear();
    }
  };
}